/**
 * `<LayoutPanel>` — the dropdown under the count button: which layouts exist,
 * what shape this one is, and what it syncs.
 *
 * ## The arrangements are drawn, not iconified
 *
 * The reference product lists every count from 1 to 16 as a row of 16px
 * outlines differing by one internal line. That works for someone who already
 * knows which one they want; a new user reads them one at a time, because the
 * glyphs are not selectable at a glance. So there are fewer of them, each drawn
 * large enough to read and **named in words** — "2 across" is something you can
 * say out loud, search for, or be told over a call.
 *
 * Each glyph is a CSS grid built from the template's own `cells`. Hand-drawn
 * SVGs would be a second description of geometry that already exists as data,
 * and the two would drift the first time a template changed.
 *
 * ## Sync: the flags, not the behaviour
 *
 * Toggling one writes `Layout.sync` and stops there. Fanning a `setSymbol` out
 * to every chart in the layout belongs in the reducer — one dispatch, one save,
 * every caller unaware that sync exists — and that change has not been made
 * yet. See `docs/plans/layouts.md`.
 *
 * **Crosshair is a flag here, but its position is not.** The other flags sync a
 * *persisted* property — symbol, interval and bar range are `ChartGroup`
 * fields, so "synced" means one dispatch fans out and the result is saved. A
 * crosshair position is a pointer coordinate sixty times a second, so it rides
 * `lib/crosshair-link.ts`, a runtime channel between mounted charts; only the
 * tick that turns that channel on is stored. It is the one flag that **defaults
 * on** (owner, 2026-10-05), so the box reads absent as ticked.
 *
 * ## Positioning
 *
 * `position: fixed`, measured off the trigger, exactly as in `GroupBar.tsx` and
 * `IntervalPicker.tsx`. **Not `absolute`**: the row above sets
 * `overflow: hidden`, and an absolutely-positioned child of one is clipped by
 * it. That has now bitten this codebase three times.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { mintUlid, type Layout, type SyncFlags } from "@qufolio/model";
import { crosshairLinked } from "../lib/crosshair-link";

import type { LayoutSummary } from "../api/layouts";
import type { GroupAccount } from "../api/me";
import { promotePlan, publishedDay, type SharedState } from "../lib/group-layouts";
import { PublishedMenu } from "./PublishedMenu";
import { PromoteDialog } from "./PromoteDialog";
import { newChartSection } from "../lib/chart-group";
import { bucketsBriefLayout } from "../lib/buckets-brief";
import { briefBoardLayout } from "../lib/brief-board";
import { dataLayout } from "../lib/data-layout";
import {
  MACRO_KIND_BLURB,
  MACRO_KIND_TITLE,
  MACRO_KINDS,
  calendarLayout,
  macroKindsToAdd,
  macroLayout,
  type MacroKind,
} from "../lib/macro-sections";
import { can } from "../lib/entitlements";
import { chartGroupConfig } from "../lib/section-config";
import { isSymbolLocked, layoutLockState } from "../lib/symbol-lock";
import { canHide, hiddenCount, isHidden } from "../lib/layout-visibility";
import { JOURNAL_TAB, tabOrder, tabStrip } from "../lib/tab-strip";
import {
  LAYOUT_TEMPLATES,
  SCREENER_PLACEMENTS,
  applyTemplate,
  chartsDropped,
  chartsOf,
  matchTemplate,
  placeScreener,
  screenerPlacement,
  templateCount,
  popoverTemplates,
  type LayoutTemplate,
  type ScreenerPlacement,
} from "../lib/layout-templates";

export interface LayoutPanelProps {
  /** The count button. The panel measures off it and hands focus back to it. */
  anchorRef: React.RefObject<HTMLElement | null>;
  layouts: readonly Layout[];
  /** Index of the layout on screen; `-1` when the workspace has none. */
  layoutIndex: number;
  onSelect: (index: number) => void;
  onRename: (layoutId: string, name: string) => void;
  /** An arrangement applied — the whole layout, already rearranged. */
  onReplace: (layout: Layout) => void;
  /**
   * Give the layout on screen a screener on `placement`, when it has none.
   * The host's: a new screener is seeded from the workspace (the panel does not
   * hold it), and a leftover dock has to open on it — the strip's Add screener,
   * with a side. Absent hides the Screener row on a layout without one.
   */
  onAddScreener?: (placement: ScreenerPlacement) => void;
  onAdd: (layout: Layout) => void;
  /**
   * Keep a layout off the tab strip, or put it back.
   *
   * The caller owns the selection: hiding the layout on screen has to move it,
   * and only `App` knows where it is. See `lib/layout-visibility.ts`.
   */
  onSetHidden: (layoutId: string, hidden: boolean) => void;
  /**
   * The row's symbol-lock switch (owner, 2026-10-06): lock every chart's symbol
   * in the layout, or unlock them all. Absent, no switch is drawn.
   */
  onSetSymbolLocked?: (layoutId: string, locked: boolean) => void;
  /** Delete it. Already confirmed here — the caller does not ask again. */
  onRemove: (layoutId: string) => void;
  onSync: (layoutId: string, sync: SyncFlags) => void;
  /**
   * Show the trade journal instead of the charts.
   *
   * View state (D8), not workspace data — which is why it is a toggle here and
   * not a `Section` in the layout. It lives in this panel because this is where
   * *what is on screen* is chosen: the arrangements, the layout list, and now
   * this. Omitted entirely when there is no journal server to ask.
   */
  showTrades?: boolean;
  onShowTrades?: (show: boolean) => void;
  /**
   * Where the journal sits in the strip, and whether it is put away.
   *
   * Workspace data, unlike `showTrades` above — which tab is *selected* is a
   * session fact, and where a tab *sits* is a choice the user made and expects
   * to find again. See `lib/tab-strip.ts`.
   */
  journalIndex?: number;
  journalHidden?: boolean;
  /** A tab dragged, as two positions in `tabOrder`. */
  onMoveTab?: (from: number, to: number) => void;
  /** The journal has no delete, so hiding is the only way to put it away. */
  onSetJournalHidden?: (hidden: boolean) => void;
  /**
   * The club a publish would belong to, or absent for no publish control.
   *
   * Two facts decided in `App`, both from `/auth/me`: a non-admin gets no
   * control, and an admin in no club gets none either, because there would be
   * nobody to publish *to*. Exactly the rule the screener's Publish already
   * follows — one answer to "who may publish", not two.
   */
  publishTarget?: string | null;
  /**
   * Publish, resolving to the version the server recorded — the same number
   * when nothing changed, which `PublishedMenu` reads as "No changes".
   */
  onPublish?: (input: { name: string; layout: Layout }) => Promise<number | undefined | void>;
  /** The publish target as the author reads it — `JP`. Falls back to the key. */
  publishTargetName?: string | null;
  /** Re-read the listing after a Restore, so the row's version is current. */
  onPublishedChanged?: () => Promise<void>;
  /**
   * Withdraw one this club holds — the same control, clicked again.
   *
   * **Parked, not deleted.** It stops appearing in members' pickers and the key
   * survives, so re-publishing puts it back and the grants that name it never
   * had to be re-issued. Absent, a published row is marked and nothing more.
   */
  onUnpublish?: (key: string) => Promise<void>;
  /**
   * Save the arrangement on screen as one of the caller's own templates.
   *
   * Absent with no server to ask, so the control is not offered rather than
   * offered and failing on click.
   */
  onSaveTemplate?: (input: { name: string; layout: Layout }) => Promise<void>;
  /** Delete one of the caller's own templates. Absent, the rows carry no ×. */
  onRemoveTemplate?: (key: string) => Promise<void>;
  /** Arrangements this account may apply — their own, a club's, the product's. */
  layoutPresets?: readonly LayoutSummary[];
  onApplyPreset?: (key: string, name: string) => void | Promise<void>;
  /**
   * Authoring a virtual group: the included groups' layouts, read-only, with
   * the screener override per layout (`InheritedLayouts`). The host builds it,
   * because only the host knows the includes and the override routes.
   */
  inheritedSlot?: React.ReactNode;
  /**
   * Each group layout's state for this member, by key — `behind` and `new`
   * put ● new on its Shared with me row (`group-layouts.sharedState`).
   */
  sharedStates?: ReadonlyMap<string, SharedState>;
  /**
   * The groups this account may **Promote & Publish** to. Empty or absent —
   * nobody's group admin, or authoring as a group already — and the button
   * is not there.
   */
  promoteTo?: readonly GroupAccount[];
  onPromote?: (account: GroupAccount, layout: Layout, name: string) => Promise<number | undefined>;
  /** + Add section: one macro section appended to a layout. Absent, no row. */
  onAddSection?: (layoutId: string, kind: MacroKind) => void;
  onClose: () => void;
}

const SYNC_TOGGLES: ReadonlyArray<{ key: keyof SyncFlags; label: string }> = [
  { key: "symbol", label: "Symbol" },
  { key: "frequency", label: "Interval" },
  { key: "barRange", label: "Date range" },
  { key: "chartType", label: "Chart type" },
  { key: "barSource", label: "Bar source" },
  { key: "crosshair", label: "Crosshair" },
];

/** The one-chart arrangement, which is also what a brand-new layout is. */
const SINGLE = LAYOUT_TEMPLATES.find((t) => templateCount(t) === 1)!;

/**
 * The published arrangements, split by who owns them.
 *
 * **A club's and the product's are not one list**, though they arrive in one:
 * `GET /layouts` answers with both, and listing them under "From your club"
 * told every account that belongs to no club — which is most of them — that a
 * club it is not in had published `Classic`.
 *
 * Order is deliberate: **yours first**, because they are the ones you made and
 * the ones you reach for; then a club's, which somebody chose for this account;
 * then the product's, which are the floor everyone has.
 */
const PRESET_GROUPS: ReadonlyArray<{ kind: LayoutSummary["ownerKind"]; label: string }> = [
  { kind: "user", label: "My templates" },
  { kind: "org", label: "Shared with me" },
  { kind: "system", label: "From QuFolio" },
];

export function LayoutPanel({
  anchorRef,
  layouts,
  layoutIndex,
  onSelect,
  onRename,
  onReplace,
  onAddScreener,
  onAdd,
  onSetHidden,
  onSetSymbolLocked,
  onRemove,
  onSync,
  showTrades,
  onShowTrades,
  journalIndex,
  journalHidden,
  onMoveTab,
  onSetJournalHidden,
  publishTarget,
  publishTargetName,
  onPublishedChanged,
  onPublish,
  onUnpublish,
  onSaveTemplate,
  onRemoveTemplate,
  layoutPresets,
  onApplyPreset,
  inheritedSlot,
  sharedStates,
  promoteTo,
  onPromote,
  onAddSection,
  onClose,
}: LayoutPanelProps) {
  const current = layouts[layoutIndex];
  /** Promote & Publish: the confirm on screen, and the last one's result line. */
  const [promoting, setPromoting] = useState(false);
  const [promoted, setPromoted] = useState<string | null>(null);
  /** Which layout's name is being edited, and the text so far. */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  /** An arrangement waiting on the confirm below, because it would drop charts. */
  const [pending, setPending] = useState<LayoutTemplate | null>(null);
  /**
   * Whether the hidden layouts are listed.
   *
   * Collapsed by default and view state either way: putting a layout away and
   * then reading a list of everything you put away are different visits, and the
   * common one is the panel opening short. Opened automatically for nobody —
   * see the disclosure line's own comment.
   */
  const [showHidden, setShowHidden] = useState(false);
  /** A layout waiting on the delete confirm, by id. Nothing is destroyed twice. */
  const [removing, setRemoving] = useState<string | null>(null);

  /**
   * Which of my layouts this club already holds, so a row can say so.
   *
   * **Matched on the name, which is a real limitation and is why it only ever
   * decorates.** Publishing copies the arrangement into a `layout_preset` row
   * and nothing links the two back — so this can be wrong in both directions: a
   * layout renamed after publishing stops being marked, and a layout that
   * happens to share a name with a club publication is marked without having
   * been the source of it.
   *
   * It is used for the marker and the wording of the tooltip, never to decide
   * whether the control is offered or what it does, so being wrong costs a
   * colour rather than a publish. The honest fix is a `publishedKey` on the
   * `Layout`, which is a contract change and is not worth one for a tint.
   */
  /** Which published layout's ⇪ menu is open, by preset key. */
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const publishedVersions = useMemo(
    () =>
      new Map(
        (layoutPresets ?? [])
          .filter((p) => p.ownerKind === "org" && p.ownerKey === publishTarget)
          .map((p) => [p.key, p.version] as const),
      ),
    [layoutPresets, publishTarget],
  );

  const publishedNames = useMemo(
    () =>
      new Map(
        (layoutPresets ?? [])
          .filter((p) => p.ownerKind === "org" && p.ownerKey === publishTarget)
          .map((p) => [p.name, p.key] as const),
      ),
    [layoutPresets, publishTarget],
  );

  // The journal is a tab, so a hidden journal is one of the things "2 hidden"
  // is counting — otherwise the disclosure says 0 while holding a row.
  const putAway = hiddenCount(layouts) + (journalHidden === true ? 1 : 0);

  /**
   * The drag in flight: where it started, and the row it is over.
   *
   * Two pieces of state rather than one, because the row being dragged *over*
   * is what draws the drop line and the row picked *up* is what dims — and a
   * drag that ends outside the list has to clear both. Positions are into
   * `tabOrder`, never into the rendered list: the hidden layouts are still in
   * the order, and renumbering around them is what sends a drop to the wrong
   * place.
   */
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const order = tabOrder(layouts, journalIndex);
  const positionOf = (id: string) => order.indexOf(id);
  const dragging = onMoveTab !== undefined && order.length > 1;

  const endDrag = () => {
    setDragFrom(null);
    setDragOver(null);
  };

  /**
   * The handlers that make one row a drop target.
   *
   * `preventDefault` on dragover is what *permits* a drop — without it the
   * browser rejects every one and the drag ends with nothing happening and no
   * error, which is the single most common way a hand-rolled drag list fails.
   */
  const dropTarget = (position: number) =>
    dragging
      ? {
          onDragOver: (event: React.DragEvent) => {
            if (dragFrom === null) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = "move" as const;
            if (dragOver !== position) setDragOver(position);
          },
          onDrop: (event: React.DragEvent) => {
            event.preventDefault();
            if (dragFrom !== null && dragFrom !== position) onMoveTab?.(dragFrom, position);
            endDrag();
          },
        }
      : {};

  /** The grip. `draggable` lives here and not on the row, so the buttons in a
      row stay clickable and a drag can only start from the thing that looks
      like a handle. */
  function handle(position: number, label: string) {
    if (!dragging) return null;
    return (
      <span
        draggable
        role="button"
        tabIndex={-1}
        aria-label={`Reorder ${label}`}
        title="Drag to reorder"
        onDragStart={(event) => {
          setDragFrom(position);
          event.dataTransfer.effectAllowed = "move";
          // Some browsers refuse to start a drag with no payload at all.
          event.dataTransfer.setData("text/plain", String(position));
        }}
        onDragEnd={endDrag}
        style={GRIP}
      >
        ⠿
      </span>
    );
  }

  /** The line that says where a drop would land. */
  const dropStyle = (position: number): React.CSSProperties =>
    dragOver === position && dragFrom !== null && dragFrom !== position
      ? { boxShadow: "inset 0 2px 0 0 var(--qf-accent)" }
      : {};

  const dragStyle = (position: number): React.CSSProperties =>
    dragFrom === position ? { opacity: 0.45 } : {};

  /**
   * A new chart starts on whatever the layout is already showing.
   *
   * A hardcoded ticker in the second pane of a split is a worse first frame
   * than a duplicate of the first, and changing it is one click.
   */
  const firstChart = current ? chartsOf(current)[0] : undefined;
  const seed = firstChart && chartGroupConfig(firstChart);
  const makeChart = () => newChartSection(seed?.symbol, seed?.frequency);

  const applyToCurrent = (template: LayoutTemplate) => {
    if (!current) return;
    onReplace(applyTemplate(current, template, makeChart));
    close();
  };

  /**
   * Exactly one dropped chart applies straight away.
   *
   * A confirm for it would be a dialog guarding a single click that is visibly
   * about to remove a chart; past one, what is being destroyed is several charts
   * someone configured, and a silent truncation is the thing that is not
   * forgiven.
   */
  const choose = (template: LayoutTemplate) => {
    if (!current) return;
    if (chartsDropped(current, template) > 1) setPending(template);
    else applyToCurrent(template);
  };

  const startRename = (layout: Layout) => {
    setEditing(layout.id);
    setDraft(layout.name);
  };

  /**
   * Commit the rename, unless it is empty.
   *
   * An empty name gives the tab strip nothing to render, and the tab is the only
   * way back to a layout — so a nameless one is stranded. Reverting is the only
   * correction that does not invent a name the user did not choose.
   */
  const commitRename = () => {
    if (!editing) return;
    const name = draft.trim();
    if (name) onRename(editing, name);
    setEditing(null);
  };

  /**
   * Dismiss, committing a rename still sitting in the box.
   *
   * A pointerdown outside the panel is handled in the capture phase — before
   * the browser has moved focus — so the input's own `onBlur` never runs, and a
   * name that was typed would be thrown away without a sign. Committing here is
   * what makes "click away" mean one thing wherever you click.
   */
  const close = () => {
    commitRename();
    onClose();
  };

  const select = (index: number) => {
    onSelect(index);
    close();
  };

  /**
   * The publish form: absent, or the name being typed into it.
   *
   * A form rather than a straight click, because publishing needs a name and the
   * layout's own is the club's opening offer rather than the answer — "Layout 3"
   * is what a member called it, and a club shelf full of those helps nobody.
   * Seeded with it anyway, so the usual case is Enter.
   */
  /** The template name being typed, or null when the box is shut. */
  const [saving, setSaving] = useState<string | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [publishing, setPublishing] = useState<string | null>(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  const startSaveTemplate = () => {
    setSaveError(null);
    // Pre-filled with the layout's own name, which is the one somebody means
    // nine times in ten — and `uq_preset_owner_name` refuses a second template
    // of the same name, so the error it produces is about the right thing.
    setSaving(current?.name ?? "");
  };

  const commitSaveTemplate = async () => {
    const name = (saving ?? "").trim();
    if (!current || !onSaveTemplate || !name || saveBusy) return;
    setSaveBusy(true);
    setSaveError(null);
    try {
      await onSaveTemplate({ name, layout: current });
      setSaving(null);
    } catch (error) {
      // Shown, never swallowed: somebody who pressed Save and saw the form go
      // would believe the arrangement was kept and go looking for it later.
      setSaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setSaveBusy(false);
    }
  };

  const startPublish = () => {
    setPublishError(null);
    setPublishing(current?.name ?? "");
  };

  const commitPublish = async () => {
    const name = (publishing ?? "").trim();
    if (!current || !onPublish || !name || publishBusy) return;
    setPublishBusy(true);
    setPublishError(null);
    try {
      await onPublish({ name, layout: current });
      setPublishing(null);
    } catch (error) {
      // Shown, never swallowed: somebody who pressed Publish and saw the form
      // close would tell the club it was there.
      setPublishError(error instanceof Error ? error.message : String(error));
    } finally {
      setPublishBusy(false);
    }
  };

  const addLayout = () => {
    const bare: Layout = {
      id: mintUlid(),
      // `Layout 3` needs no apology: renaming it is one click away in the panel
      // that just created it, and naming must never block creating.
      name: `Layout ${layouts.length + 1}`,
      rows: 1,
      columns: 1,
      rowSizes: ["1fr"],
      columnSizes: ["1fr"],
      sections: [],
    };
    // Through `applyTemplate` rather than assembling the section here, so "a new
    // layout" and "an arrangement applied" are the same code path and cannot
    // produce two different one-chart layouts.
    onAdd(applyTemplate(bare, SINGLE, makeChart));
    close();
  };

  /**
   * One row of the layout list — shown or hidden, current or not.
   *
   * A function rather than a component because the rename lives in this
   * component's state and a child would need six props to reach it, three of
   * which are setters. The two lists below call it with the same `index` the
   * array has: hiding reshapes nothing, so an index is still what `onSelect`
   * wants, and filtering the *rendering* must not renumber it.
   */
  function row(layout: Layout, index: number) {
    const isCurrent = index === layoutIndex;
    const hidden = isHidden(layout);
    const charts = chartsOf(layout).length;

    // The confirm replaces the row rather than opening beneath it — the
    // arrangement confirm above does the same, and a panel that grows under the
    // pointer moves everything else the user was about to click.
    if (removing === layout.id) {
      return (
        <div key={layout.id} style={CONFIRM}>
          <span role="alert" style={NAME_TEXT}>
            {`Delete ${layout.name} and its ${charts} chart${charts === 1 ? "" : "s"}?`}
          </span>
          <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
            <button type="button" onClick={() => setRemoving(null)} style={QUIET_BUTTON}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                setRemoving(null);
                onRemove(layout.id);
              }}
              style={DANGER_BUTTON}
            >
              Delete
            </button>
          </div>
        </div>
      );
    }

    const position = positionOf(layout.id);
    return (
      <div
        key={layout.id}
        style={{
          ...listRowStyle(isCurrent),
          ...dropStyle(position),
          ...dragStyle(position),
        }}
        {...dropTarget(position)}
      >
        {handle(position, layout.name)}
        {editing === layout.id ? (
          <input
            autoFocus
            aria-label="Layout name"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitRename();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                // Nothing above may also act on this: an Escape that
                // bubbles would abandon the rename *and* close the panel.
                event.stopPropagation();
                setEditing(null);
              }
            }}
            onBlur={commitRename}
            style={NAME_INPUT}
          />
        ) : (
          <button
            type="button"
            aria-current={isCurrent ? "true" : undefined}
            // Clicking the current layout's name opens the rename:
            // selecting the layout you are already in does nothing, so the
            // click has nothing else it could mean.
            //
            // **A hidden layout selects instead of renaming, even when it is
            // the current one.** That pairing is reachable — hide the last one
            // from another machine — and there the click has an obvious job.
            onClick={() =>
              isCurrent && !hidden ? startRename(layout) : select(index)
            }
            title={isCurrent && !hidden ? "Rename" : `Show ${layout.name}`}
            style={hidden ? { ...NAME_BUTTON, ...DIMMED } : NAME_BUTTON}
          >
            <span style={NAME_TEXT}>{layout.name}</span>
            <RowTail
              count={charts}
              // Only while authoring for a group, where a version means
              // something: the layout's publication, matched on the name for
              // the reason `publishedNames` gives.
              {...(publishTarget
                ? {
                    version: {
                      number: (() => {
                        const key = publishedNames.get(layout.name);
                        return key === undefined ? undefined : publishedVersions.get(key);
                      })(),
                      groupName: publishTargetName ?? publishTarget,
                    },
                  }
                : {})}
            />
          </button>
        )}

        {/* **The symbol-lock switch** (owner, 2026-10-06; the mock puts it
            first: 🔒 ✎ ⇪ ⊘ ×). Locks every chart's symbol in the layout, or
            unlocks them all; half-strength 🔒 when some are and some are not,
            and a click there locks the rest — the usual tri-state rule.
            **Current row only**, beside ✎, ⇪ and × which are too: the other
            rows keep their width, and the panel is a fixed column whose names
            already ellipsise. Per-chart locks are on each chart's own header. */}
        {isCurrent && editing !== layout.id && onSetSymbolLocked && (() => {
          const state = layoutLockState(layout);
          if (state === "empty") return null;
          const locked = chartsOf(layout).filter((s) => isSymbolLocked(chartGroupConfig(s))).length;
          return (
            <button
              type="button"
              aria-label={
                state === "all"
                  ? `Unlock every chart in ${layout.name}`
                  : `Lock every chart in ${layout.name}`
              }
              aria-pressed={state === "all" ? true : state === "some" ? "mixed" : false}
              title={
                state === "all"
                  ? "Every chart's symbol is locked — click to unlock them all"
                  : state === "some"
                    ? `${locked} of ${charts} charts locked — click to lock them all`
                    : "Lock every chart's symbol — a screener click or typing will not replace it"
              }
              onClick={() => onSetSymbolLocked(layout.id, state !== "all")}
              style={{
                ...ICON,
                fontSize: 11,
                ...(state === "some" ? MIXED : state === "none" ? { color: "var(--qf-text-muted)" } : {}),
              }}
            >
              {state === "none" ? "🔓" : "🔒"}
            </button>
          );
        })()}

        {isCurrent && !hidden && editing !== layout.id && (
          // The pencil is the discoverable half of the same gesture — the
          // name being clickable is not visible until you try it.
          <button
            type="button"
            aria-label="Rename layout"
            title="Rename"
            onClick={() => startRename(layout)}
            style={PENCIL}
          >
            ✎
          </button>
        )}

        {/* **Publish lives on the row, beside rename and hide and delete.**
            It was a bare `Publish…` sharing the new-layout row, which made
            *what* it published implicit — it acted on whichever layout you
            happened to be in, nothing said so, and sitting next to a control
            that creates a layout it read as though it might publish that one.
            On the row, the target is the thing it is attached to.

            Absent, never disabled, for anyone who cannot publish — the rule
            the screener's Publish already follows, because a greyed control
            invites a hunt for the reason it is greyed. */}
        {isCurrent && editing !== layout.id && publishTarget && onPublish && (() => {
          // A layout linked to one of this group's (`source`) is that one even
          // renamed — the key a publish goes to (`publishedKeyFor`), so the
          // marker and the publish cannot name different layouts.
          const linked = layout.source?.preset;
          const publishedKey =
            (linked !== undefined && publishedVersions.has(linked) ? linked : undefined) ??
            publishedNames.get(layout.name);
          const groupName = publishTargetName ?? publishTarget;
          return (
            <span style={{ position: "relative", display: "inline-flex" }}>
            <button
              type="button"
              aria-label={
                publishedKey
                  ? `${layout.name} is published to ${groupName}`
                  : `Publish ${layout.name} to ${groupName}`
              }
              aria-expanded={publishedKey ? menuFor === publishedKey : undefined}
              title={
                publishedKey
                  ? `Published to ${groupName} — publish changes, history, withdraw`
                  : `Publish this layout to ${groupName}`
              }
              // **Withdrawing acts at once, like the eye beside it.** It is
              // reversible in one click and the key survives, so the grants
              // that name it never have to be re-issued — there is nothing to
              // confirm that re-publishing does not undo. Publishing still
              // opens the name box, because it needs a name.
              // Published: ⇪ opens the menu — publish the draft again, history,
              // restore, withdraw. It used to withdraw in one click, which left
              // no way to push an update (group-accounts.md §5).
              onClick={() =>
                publishedKey
                  ? setMenuFor((open) => (open === publishedKey ? null : publishedKey))
                  : startPublish()
              }
              style={publishedKey ? { ...ICON, color: "var(--qf-positive)" } : ICON}
            >
              ⇪
            </button>
            {publishedKey && menuFor === publishedKey && current && (
              <PublishedMenu
                kind="layouts"
                presetKey={publishedKey}
                groupName={groupName}
                {...(publishedVersions.get(publishedKey) !== undefined
                  ? { current: publishedVersions.get(publishedKey)! }
                  : {})}
                onPublish={async () =>
                  (await onPublish({ name: layout.name, layout: current })) ?? undefined
                }
                onRestored={() => onPublishedChanged?.()}
                {...(onUnpublish ? { onWithdraw: () => onUnpublish(publishedKey) } : {})}
                onClose={() => setMenuFor(null)}
              />
            )}
            </span>
          );
        })()}

        {/* **On every row, because hiding a layout you are not in is the whole
            point.** Disabled rather than absent on the last shown one: a
            control that vanishes leaves nothing to explain why, and the reason
            is worth a sentence. */}
        <button
          type="button"
          aria-label={hidden ? `Show ${layout.name}` : `Hide ${layout.name}`}
          aria-pressed={hidden}
          disabled={!hidden && !canHide(layouts, index)}
          title={
            hidden
              ? "Show in the tab strip"
              : canHide(layouts, index)
                ? "Hide from the tab strip — the layout is kept"
                : "The last layout on the strip cannot be hidden"
          }
          onClick={() => onSetHidden(layout.id, !hidden)}
          style={
            !hidden && !canHide(layouts, index) ? { ...ICON, ...DISABLED } : ICON
          }
        >
          {hidden ? "⊙" : "⊘"}
        </button>

        {/* **Only on the layout you are looking at**, which is also the only
            one whose charts are on screen while the confirm names how many go.
            Deleting is the one gesture here with no inverse — `addLayout` puts
            a *different* layout back, with different ids throughout — so it is
            worth costing a click to reach.

            Absent, not disabled, on the last layout: a workspace with no
            layouts has nothing to render and no control to fix it with, and
            there is no state in which the button becomes available. */}
        {isCurrent && editing !== layout.id && layouts.length > 1 && (
          <button
            type="button"
            aria-label={`Delete ${layout.name}`}
            title="Delete this layout"
            onClick={() => setRemoving(layout.id)}
            style={{ ...ICON, color: "var(--qf-negative)" }}
          >
            ×
          </button>
        )}
      </div>
    );
  }

  /**
   * The journal's row — a tab with no layout behind it.
   *
   * It carries a grip and an eye and nothing else: there is no name to rename,
   * no chart count to report, and **no delete**, because the journal is not
   * something a workspace can be without. Hiding is therefore the only way to
   * put it away, which is why the eye is offered on a row that has no ×.
   */
  function journalRow() {
    const position = positionOf(JOURNAL_TAB);
    const hidden = journalHidden === true;
    return (
      <div
        key={JOURNAL_TAB}
        style={{
          ...listRowStyle(showTrades === true),
          ...dropStyle(position),
          ...dragStyle(position),
        }}
        {...dropTarget(position)}
      >
        {handle(position, "Journal")}
        <button
          type="button"
          onClick={() => {
            onShowTrades?.(true);
            close();
          }}
          title="Show the journal"
          style={hidden ? { ...NAME_BUTTON, ...DIMMED } : NAME_BUTTON}
        >
          <span style={NAME_TEXT}>Journal</span>
        </button>
        <button
          type="button"
          aria-label={hidden ? "Show Journal" : "Hide Journal"}
          aria-pressed={hidden}
          title={
            hidden ? "Show in the tab strip" : "Hide from the tab strip — the journal is kept"
          }
          onClick={() => onSetJournalHidden?.(!hidden)}
          style={ICON}
        >
          {hidden ? "⊙" : "⊘"}
        </button>
      </div>
    );
  }

  /**
   * Every row the list draws, in strip order, filtered to one hidden state.
   *
   * Strip order rather than array order, because the order *is* what the drag
   * edits — a list that drew the layouts in one order and reordered them in
   * another would be unusable. The journal is one of these rows, which is the
   * whole of "the journal is a tab": nothing here treats it as a mode.
   */
  function rows(hidden: boolean) {
    return tabStrip(layouts, journalIndex).map((tab) =>
      tab.kind === "journal"
        ? (journalHidden === true) === hidden
          ? journalRow()
          : null
        : isHidden(tab.layout) === hidden
          ? row(tab.layout, tab.index)
          : null,
    );
  }

  const matched = current && matchTemplate(current);
  /**
   * Read back out of the grid, never stored — see `screenerPlacement`. So a
   * layout dragged into a shape no side describes ticks nothing rather than
   * claiming a side it does not have.
   */
  const placement = current && screenerPlacement(current);
  const hasScreener = current?.sections.some((s) => s.kind !== "chartGroup") ?? false;
  /**
   * No screener yet: the same four sides *add* one there (owner, 2026-11-01).
   * The row was hidden until a screener existed, and the only way to add one
   * was the strip's key — a new layout showed no screener control here at all.
   * Not on a layout with no chart (a board): a screener is placed beside the
   * charts, and there are none to be beside.
   */
  const canAddScreener =
    !hasScreener && onAddScreener !== undefined && (current?.sections.some((s) => s.kind === "chartGroup") ?? false);

  return (
    <Popover anchorRef={anchorRef} onClose={close}>
      <div style={FIRST_GROUP}>
        {rows(false)}

        {/* **The count, not a list.** A layout put away is meant to be out of
            the way; naming every one of them here would undo half of what
            hiding is for, and the number is the part that says the layouts are
            still there. Collapsed on every open, including one straight after a
            hide — a list that stayed expanded because of something you did a
            minute ago is a panel that changes height for reasons you have
            forgotten. */}
        {putAway > 0 && (
          <button
            type="button"
            aria-expanded={showHidden}
            onClick={() => setShowHidden((v) => !v)}
            title={showHidden ? "Collapse" : "Show the hidden layouts"}
            style={DISCLOSURE}
          >
            <span aria-hidden style={{ width: 10 }}>{showHidden ? "▾" : "▸"}</span>
            {putAway} hidden
          </button>
        )}

        {showHidden && rows(true)}

        {/* **Promote & Publish** — below the list, above Arrangement (owner,
            2026-10-18): the current layout, from your own account, straight to
            a group's members' Shared with me. Absent for anyone with no group
            to publish to, and while authoring as one, where the row's own
            Publish is the way. */}
        {current && onPromote && !publishTarget && (promoteTo?.length ?? 0) > 0 && (
          <div style={PROMOTE_ROW}>
            <button
              type="button"
              onClick={() => {
                setPromoted(null);
                setPromoting(true);
              }}
              title="Publish this layout to a group's members — it appears in their Shared with me"
              style={PROMOTE_BUTTON}
            >
              <span aria-hidden>⇪</span> Promote &amp; Publish
              <span style={PROMOTE_NAME}>{current.name}</span>
            </button>
            {promoted && <div role="status" style={PROMOTE_DONE}>{promoted}</div>}
          </div>
        )}
        {promoting && current && onPromote && promoteTo && (
          <PromoteDialog
            layoutName={current.name}
            targets={promoteTo.map((account) => ({
              account,
              plan: promotePlan(layoutPresets ?? [], account.group, current, current.name),
            }))}
            onConfirm={(account) => onPromote(account, current, current.name)}
            onDone={({ account, version }) => {
              setPromoting(false);
              setPromoted(
                `✓ Published to ${account.name}${version ? ` (v${version})` : ""} · ${publishedDay(undefined, new Date()) ?? ""}`,
              );
            }}
            onCancel={() => setPromoting(false)}
          />
        )}
      </div>

      {current && (
        <div style={GROUP}>
          <div style={SECTION_LABEL}>Arrangement</div>
          {pending ? (
            <div style={CONFIRM}>
              {/* An alert, so it is announced when it replaces the arrangements
                  rather than being something a screen reader has to go looking
                  for after a click that appeared to do nothing. */}
              <span role="alert">{`Discard ${chartsDropped(current, pending)} charts?`}</span>
              <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
                <button type="button" onClick={() => setPending(null)} style={QUIET_BUTTON}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPending(null);
                    applyToCurrent(pending);
                  }}
                  style={DANGER_BUTTON}
                >
                  Discard
                </button>
              </div>
            </div>
          ) : (
            /* **One wrapping row, no count headings**, five tiles a line —
               stacked into count-groups it was six headings tall and the reason
               the screener's own buttons fell below the fold. Every arrangement
               is offered (`popoverTemplates`), in chart-count order. */
            <div style={TEMPLATE_ROW}>
              {popoverTemplates().map((template) => (
                <button
                  key={template.id}
                  type="button"
                  title={template.label}
                  aria-pressed={template.id === matched?.id}
                  onClick={() => choose(template)}
                  style={templateStyle(template.id === matched?.id)}
                >
                  <TemplateGlyph template={template} />
                  <span>{template.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {current && (hasScreener || canAddScreener) && (
        <div style={GROUP}>
          {/* Beside the arrangements because it is the same kind of choice over
              the same grid — where the sections go — and a layout is picked once
              in one visit rather than in two places. Four named sides rather
              than a four-way pad of 12px arrows: "Bottom" is a word you can say
              out loud, and the glyph beside it only has to confirm it. */}
          <div style={SECTION_LABEL}>Screener</div>
          <div style={PLACEMENT_ROW}>
            {SCREENER_PLACEMENTS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                aria-pressed={hasScreener && id === placement}
                title={hasScreener ? `Screener on the ${label.toLowerCase()}` : `Add a screener on the ${label.toLowerCase()}`}
                onClick={() => {
                  if (hasScreener) onReplace(placeScreener(current, id));
                  else onAddScreener?.(id);
                  close();
                }}
                style={placementStyle(hasScreener && id === placement)}
              >
                <PlacementGlyph placement={id} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={GROUP}>
        {/* Inside the panel rather than a `+` on the tab strip: a `+` beside
            tabs means "add a tab" and nothing else, where here it sits next to
            the arrangement list — so creating a layout and choosing its shape
            are one visit rather than two.

            **It has the row to itself now.** Publish used to share it, which
            is what made Publish's target implicit; it is on the layout row
            instead, where the thing it acts on is the thing it is attached to.

            **Centred, with a `+`.** Left-aligned text in a bordered box with no
            glyph is the shape of a *text input*, and it sat beside a Publish
            that centred its own — so the pair read as "type a name here, then
            press that", and clicking the box appeared to do nothing. It has
            always been a button; it now looks like one. */}
        {/* **Under New rather than beside it**, because they are different acts
            on different things: one makes an empty layout, the other keeps the
            one you are looking at. Side by side they read as a pair of ways to
            get a layout, which is how Publish came to be confusing. */}
        {onSaveTemplate && current && (
          <div style={{ ...NEW_ROW, paddingBottom: 6 }}>
            <button
              type="button"
              onClick={startSaveTemplate}
              title={`Keep "${current.name}" to start new layouts from`}
              style={{ ...NEW_BUTTON, flex: "1 1 auto", textAlign: "center" }}
            >
              Save as template…
            </button>
          </div>
        )}

        {saving !== null && (
          <div style={{ paddingBottom: 6 }}>
            <input
              autoFocus
              aria-label="Template name"
              value={saving}
              placeholder="Name this template"
              onChange={(event) => setSaving(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void commitSaveTemplate();
                }
                if (event.key === "Escape") {
                  // Stopped, as the rename box stops it: an Escape that bubbled
                  // would abandon the name *and* close the panel.
                  event.preventDefault();
                  event.stopPropagation();
                  setSaving(null);
                }
              }}
              style={NAME_INPUT}
            />
            <div style={{ ...NEW_ROW, paddingTop: 6 }}>
              <button
                type="button"
                onClick={() => void commitSaveTemplate()}
                disabled={saveBusy || !saving.trim()}
                style={{ ...NEW_BUTTON, flex: "1 1 auto", textAlign: "center" }}
              >
                {saveBusy ? "Saving…" : "Save template"}
              </button>
              <button
                type="button"
                onClick={() => setSaving(null)}
                style={{ ...NEW_BUTTON, width: "auto", flex: "0 0 auto", textAlign: "center" }}
              >
                Cancel
              </button>
            </div>
            {saveError && <div style={PUBLISH_ERROR}>{saveError}</div>}
          </div>
        )}

        <div style={NEW_ROW}>
          <button
            type="button"
            onClick={addLayout}
            // The `+` is decoration, so it stays out of the accessible name:
            // a screen reader saying "plus new layout" is reading punctuation
            // aloud, and the name is what every test and every voice control
            // addresses this control by.
            aria-label="New layout"
            title="Add a layout and name it"
            style={{ ...NEW_BUTTON, flex: "1 1 auto", textAlign: "center" }}
          >
            <span aria-hidden>+ </span>New layout
          </button>
          {/* The data panel's views as a grid of cells (owner, 2026-10-29;
              docs/features/pip-and-data-panel.md, Data layouts): 3×3, the nine
              views in tab order, rearranged like any layout after that. */}
          <button
            type="button"
            onClick={() => {
              onAdd(dataLayout("Data"));
              close();
            }}
            aria-label="New data layout"
            title="A layout of data panel views — Profile, Fin St, News… — following the chart you click"
            style={{ ...NEW_BUTTON, flex: "1 1 auto", textAlign: "center" }}
          >
            <span aria-hidden>+ </span>New data layout
          </button>
          {/* JAS, and only while authoring for the group: a member gets the
              Buckets Brief and Daily Brief the author publishes, under Shared
              with me, so a button minting a second, unpublished copy is
              noise there (owner, 2026-10-18). Absent rather than locked. */}
          {can("ind:JAS") && publishTarget && (
            <button
              type="button"
              onClick={() => {
                onAdd(bucketsBriefLayout("Buckets Brief"));
                close();
              }}
              aria-label="New Buckets Brief layout"
              title="A layout with the JAS Bollinger distribution of four lists"
              style={{ ...NEW_BUTTON, flex: "1 1 auto", textAlign: "center" }}
            >
              <span aria-hidden>+ </span>Buckets Brief
            </button>
          )}
          {/* The Daily Brief board, on the same terms
              (`docs/features/daily-brief.md`). */}
          {can("ind:JAS") && publishTarget && (
            <button
              type="button"
              onClick={() => {
                onAdd(briefBoardLayout("Daily Brief"));
                close();
              }}
              aria-label="New Daily Brief layout"
              title="A board of market widgets: indexes, sectors, ETFs, new highs and lows"
              style={{ ...NEW_BUTTON, flex: "1 1 auto", textAlign: "center" }}
            >
              <span aria-hidden>+ </span>Daily Brief
            </button>
          )}
          {/* Macro and Calendar (`docs/plans/macro.md`): everyone's (owner, 2026-11-02). */}
          <button
            type="button"
            onClick={() => {
              onAdd(macroLayout("Macro"));
              close();
            }}
            aria-label="New Macro layout"
            title="Releases, the yield curve, what moves together, safe-haven correlations, positioning and liquidity"
            style={{ ...NEW_BUTTON, flex: "1 1 auto", textAlign: "center" }}
          >
            <span aria-hidden>+ </span>Macro
          </button>
          <button
            type="button"
            onClick={() => {
              onAdd(calendarLayout("Calendar"));
              close();
            }}
            aria-label="New Calendar layout"
            title="Economic releases and earnings, week or month"
            style={{ ...NEW_BUTTON, flex: "1 1 auto", textAlign: "center" }}
          >
            <span aria-hidden>+ </span>Calendar
          </button>
        </div>

        {/* + Add section: a macro section into the layout on screen, as a new
            bottom row. Each kind once; one already there is listed, marked,
            and not offered again (the mock's "already in this layout"). */}
        {onAddSection && current && (
          <div style={{ paddingTop: 6 }}>
            <div style={SECTION_LABEL}>Add section</div>
            {MACRO_KINDS.map((kind) => {
              const offered = macroKindsToAdd(current).includes(kind);
              return (
                <button
                  key={kind}
                  type="button"
                  disabled={!offered}
                  onClick={() => {
                    onAddSection(current.id, kind);
                    close();
                  }}
                  aria-label={`Add section ${MACRO_KIND_TITLE[kind]}`}
                  title={offered ? MACRO_KIND_BLURB[kind] : "Already in this layout"}
                  style={{ ...NEW_BUTTON, textAlign: "left", opacity: offered ? 1 : 0.5 }}
                >
                  <span aria-hidden>{offered ? "+ " : "✓ "}</span>
                  {MACRO_KIND_TITLE[kind]}
                </button>
              );
            })}
          </div>
        )}

        {publishing !== null && (
          <div style={{ paddingTop: 6 }}>
            <input
              autoFocus
              aria-label="Publish as"
              value={publishing}
              placeholder="Name it for the club"
              onChange={(event) => setPublishing(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void commitPublish();
                }
                if (event.key === "Escape") {
                  // Stopped, as the rename box stops it: an Escape that bubbled
                  // would abandon the name *and* close the panel.
                  event.preventDefault();
                  event.stopPropagation();
                  setPublishing(null);
                }
              }}
              style={NAME_INPUT}
            />
            <div style={{ ...NEW_ROW, paddingTop: 6 }}>
              <button
                type="button"
                onClick={() => void commitPublish()}
                disabled={publishBusy || !publishing.trim()}
                style={{ ...NEW_BUTTON, flex: "1 1 auto", textAlign: "center" }}
              >
                {publishBusy ? "Publishing…" : `Publish to ${publishTarget}`}
              </button>
              <button
                type="button"
                onClick={() => setPublishing(null)}
                style={{ ...NEW_BUTTON, width: "auto", flex: "0 0 auto", textAlign: "center" }}
              >
                Cancel
              </button>
            </div>
            {publishError && <div style={PUBLISH_ERROR}>{publishError}</div>}
          </div>
        )}
      </div>

      {/* **Two groups, not one heading covering both.** `system` presets have
          always come back from `GET /layouts` beside a club's and were listed
          under "From your club" with them — which reads wrong for the many
          accounts that belong to no club at all, and says a thing about the
          product that is not true.

          Applying **adds** rather than replaces, which is the one place this
          differs from the arrangements above it. An arrangement re-shapes the
          layout you are in because that is what choosing a shape means; a
          saved layout is a different desk, and replacing the one on screen
          with it would destroy charts somebody configured. */}
      {inheritedSlot}

      {onApplyPreset &&
        PRESET_GROUPS.map(({ kind, label }) => {
          const rows = (layoutPresets ?? []).filter((p) => p.ownerKind === kind);
          if (rows.length === 0) return null;
          return (
            <div key={kind} style={GROUP}>
              <div style={SECTION_LABEL}>{label}</div>
              {rows.map((preset) => (
                <div key={preset.key} style={PRESET_ROW}>
                  <button
                    type="button"
                    onClick={() => {
                      void onApplyPreset(preset.key, preset.name);
                      close();
                    }}
                    title={preset.description ?? `Add ${preset.name} as a new layout`}
                    style={NAME_BUTTON}
                  >
                    <span style={NAME_TEXT}>{preset.name}</span>
                    <RowTail
                      count={preset.chartCount}
                      // A group's publication carries its version and the day
                      // it went out; a product template or your own saved one
                      // has neither to show.
                      {...(kind === "org"
                        ? {
                            version: { number: preset.version, groupName: null },
                            day: publishedDay(preset.publishedAt),
                            fresh: ((s) => s === "new" || s === "behind")(sharedStates?.get(preset.key)),
                          }
                        : {})}
                    />
                  </button>
                  {/* **Only on your own**, which is the only group where a
                      delete means anything: a club's is theirs and the
                      product's is everyone's, and the server answers 404 to a
                      delete aimed at either. Absent rather than disabled, for
                      the reason every other control here is. */}
                  {kind === "user" && onRemoveTemplate && (
                    <button
                      type="button"
                      aria-label={`Delete template ${preset.name}`}
                      title="Delete this template — layouts made from it are untouched"
                      onClick={() => void onRemoveTemplate(preset.key)}
                      style={{ ...ICON, color: "var(--qf-negative)" }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          );
        })}

      {onShowTrades && (
        <div style={GROUP}>
          <div style={SECTION_LABEL}>Journal</div>
          <label style={TOGGLE}>
            <input
              type="checkbox"
              checked={showTrades ?? false}
              onChange={(event) => onShowTrades(event.target.checked)}
            />
            Show trades
          </label>
        </div>
      )}

      {current && (
        <div style={GROUP}>
          <div style={SECTION_LABEL}>Link across charts</div>
          {/* Two columns now the panel is wide enough: five rows was a tall
              tail under a short list of tiles. */}
          <div style={SYNC_GRID}>
            {SYNC_TOGGLES.map(({ key, label }) => (
              <label key={key} style={TOGGLE}>
                <input
                  type="checkbox"
                  checked={key === "crosshair" ? crosshairLinked(current.sync) : (current.sync?.[key] ?? false)}
                  onChange={(event) => onSync(current.id, { [key]: event.target.checked })}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}
    </Popover>
  );
}

/**
 * An arrangement drawn from its own cells.
 *
 * `aria-hidden`, because the label beside it already says "2 across" — a screen
 * reader reading out four empty boxes would be noise, and the word is the part
 * that carries the meaning anyway.
 *
 * Landscape rather than square: a chart region is wider than it is tall, and in
 * a square box "2 across" draws two vertical slivers that read as anything but
 * two charts.
 */
function TemplateGlyph({ template }: { template: LayoutTemplate }) {
  return (
    <div
      aria-hidden
      style={{
        display: "grid",
        gridTemplateRows: `repeat(${template.rows}, 1fr)`,
        gridTemplateColumns: `repeat(${template.columns}, 1fr)`,
        gap: 2,
        width: 44,
        height: 34,
      }}
    >
      {template.cells.map((cell) => (
        <span
          key={`${cell.row},${cell.column}`}
          style={{
            gridRow: `${cell.row + 1} / span ${cell.rowSpan}`,
            gridColumn: `${cell.column + 1} / span ${cell.columnSpan}`,
            border: "1px solid currentColor",
            borderRadius: 2,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Where the screener sits, drawn as the two blocks it makes: a filled bar for
 * the screener and an outline for the chart region.
 *
 * Flex rather than a grid of cells, because the only thing being said is "this
 * one first, along this axis" — `row-reverse` and `column-reverse` are exactly
 * "right" and "bottom". `aria-hidden` for the same reason as `TemplateGlyph`:
 * the word beside it already carries the meaning.
 */
function PlacementGlyph({ placement }: { placement: ScreenerPlacement }) {
  const flexDirection = (
    {
      left: "row",
      right: "row-reverse",
      top: "column",
      bottom: "column-reverse",
    } as const
  )[placement];

  return (
    <div
      aria-hidden
      style={{ display: "flex", flexDirection, gap: 2, width: 34, height: 26 }}
    >
      {/* The screener: filled, and the smaller of the two, so the picture reads
          the same way round as the layout it produces. */}
      <span style={{ flex: 1, background: "currentColor", opacity: 0.7, borderRadius: 2 }} />
      <span style={{ flex: 2.2, border: "1px solid currentColor", borderRadius: 2 }} />
    </div>
  );
}

/**
 * A dismiss-on-outside-click popover, positioned `fixed` off its trigger.
 *
 * The same shape as `GroupBar`'s and `IntervalPicker`'s, with one addition: a
 * pointerdown **on the trigger** is not an outside click. Closing here and
 * letting the button's own click reopen it is why those two cannot be closed by
 * the button that opened them.
 */
function Popover({
  anchorRef,
  children,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const panel = ref.current;
    if (!anchor || !panel) return;

    const rect = anchor.getBoundingClientRect();
    const height = panel.offsetHeight;
    const width = panel.offsetWidth;

    const below = rect.bottom + 4;
    // Flips above when there is no room below. The bar is at the top of the
    // window, so this is the unlikely half — a short viewport, or a zoom.
    const flip = below + height > window.innerHeight && rect.top - height - 4 > 0;
    const top = flip ? rect.top - height - 4 : below;
    const left = Math.max(4, Math.min(rect.left, window.innerWidth - width - 4));

    setPos({ top, left });
  }, [anchorRef]);

  useEffect(() => {
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    // `pointerdown`, not `mousedown`. SciChart calls `preventDefault()` on its
    // own pointer events, which suppresses the compatibility mouse events
    // entirely — so a `mousedown` listener never fires for a click on a chart,
    // and every popover in the app stayed open when you clicked the candles to
    // dismiss it. Capture phase as well, so a handler inside the popover that
    // stops propagation cannot hide the outside click that follows.
    document.addEventListener("pointerdown", onDown, true);

    // Bubble phase, so the rename input can keep its own Escape by stopping
    // propagation — in capture this would fire first and close the panel out
    // from under a half-typed name.
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      onClose();
      // Focus goes back where it came from, or the next Tab starts from the top
      // of the document.
      anchorRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);

    // A fixed element does not travel with a scrolling ancestor, so it would
    // detach from its trigger. Closing is honest and cheaper than tracking.
    //
    // **But not when the panel is what scrolled.** This is `scroll` in capture
    // on `window`, so it sees every scroll in the document — including this
    // panel scrolling *itself*, which it does whenever its content passes
    // `maxHeight: 70vh`. The symptom is a popover that shuts the moment you
    // reach for anything below the fold.
    //
    // **Older than it looks.** `docs/user-guide/PENDING.md` recorded the panel
    // already overflowing — "Bottom sat below the fold, and the sync checkboxes
    // are not visible at all" — and, in a parenthesis, that it "briefly stopped
    // opening at all mid-edit". Publishing and the club list made it taller and
    // so made this reliable rather than intermittent; they did not cause it.
    //
    // A window scroll reports `document` as its target and still closes; only a
    // scroll from inside is ignored.
    const onScrollOrResize = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && ref.current?.contains(target)) return;
      onClose();
    };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [anchorRef, onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Layout"
      style={{
        position: "fixed",
        // Placed off the first frame's measurement; `visibility` rather than
        // `display`, which would have no size to measure.
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        visibility: pos ? "visible" : "hidden",
        zIndex: 1000,
        // Five arrangement tiles a row: 5 × 68 + 4 × 6 gaps + 16 padding = 380,
        // plus room for a scrollbar. Capped to the viewport on a narrow screen,
        // where the tile row simply wraps sooner.
        width: "min(400px, calc(100vw - 8px))",
        maxHeight: "70vh",
        overflowY: "auto",
        background: "var(--qf-surface)",
        border: "1px solid var(--qf-border)",
        borderRadius: 6,
        boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
        color: "var(--qf-text)",
        textAlign: "left",
      }}
    >
      {children}
    </div>
  );
}

const FIRST_GROUP: React.CSSProperties = { padding: 8 };

/** Separated by a rule above rather than below, so the last group has no stray hairline. */
const GROUP: React.CSSProperties = {
  ...FIRST_GROUP,
  borderTop: "1px solid var(--qf-border)",
};

const SECTION_LABEL: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
  padding: "2px 4px 6px",
};

function listRowStyle(current: boolean): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 4,
    borderRadius: 4,
    background: current ? "var(--qf-selection)" : "transparent",
  };
}

const NAME_BUTTON: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  flex: "1 1 auto",
  font: "inherit",
  fontSize: 12,
  padding: "5px 8px",
  border: 0,
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text)",
  cursor: "pointer",
  textAlign: "left",
  minWidth: 0,
};

const NAME_TEXT: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** The drag handle: the only part of a row that starts a drag. */
const GRIP: React.CSSProperties = {
  cursor: "grab",
  color: "var(--qf-text-muted)",
  fontSize: 12,
  lineHeight: 1,
  padding: "0 2px",
  userSelect: "none",
  flexShrink: 0,
};

/**
 * A row's right-hand end: the published version, then the chart count.
 *
 * One span, so the row's `space-between` puts the pair at the end rather than
 * the version in the middle. `—` holds the column for a layout not published
 * yet, so the counts stay in a line (owner's mock, 2026-10-04).
 */
function RowTail({
  count,
  version,
  day,
  fresh,
}: {
  count: number;
  version?: { number: number | undefined; groupName: string | null };
  /** Shared with me: the day this version was published, `Oct 18`. */
  day?: string | null;
  /** Shared with me: a version this member has not taken — ● new. */
  fresh?: boolean;
}) {
  return (
    <span style={ROW_TAIL}>
      {fresh && (
        <span style={ROW_NEW} title="A version you haven't taken yet — click to see it">
          ● new
        </span>
      )}
      {version && day && <span style={ROW_DAY}>{day}</span>}
      {version && (
        <span
          style={ROW_VERSION}
          title={
            version.number === undefined
              ? "Not published yet"
              : `Published v${version.number}${version.groupName ? ` to ${version.groupName}` : ""}`
          }
        >
          {version.number === undefined ? "—" : `v${version.number}`}
        </span>
      )}
      <span style={ROW_COUNT}>{count}</span>
    </span>
  );
}

const ROW_NEW: React.CSSProperties = {
  color: "var(--qf-accent)",
  fontSize: 11,
  whiteSpace: "nowrap",
};

const ROW_DAY: React.CSSProperties = {
  color: "var(--qf-text-muted)",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

const PROMOTE_ROW: React.CSSProperties = { paddingTop: 8 };

const PROMOTE_BUTTON: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  width: "100%",
  minWidth: 0,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  borderRadius: 3,
  padding: "5px 9px",
  font: "inherit",
  fontSize: 12,
  cursor: "pointer",
  textAlign: "left",
};

const PROMOTE_NAME: React.CSSProperties = {
  marginLeft: 4,
  color: "var(--qf-text-muted)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const PROMOTE_DONE: React.CSSProperties = {
  paddingTop: 5,
  color: "var(--qf-positive)",
  fontSize: 11.5,
};

const ROW_TAIL: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  flex: "none",
};

const ROW_VERSION: React.CSSProperties = {
  color: "var(--qf-text-muted)",
  fontVariantNumeric: "tabular-nums",
  minWidth: 22,
  textAlign: "right",
};

const ROW_COUNT: React.CSSProperties = {
  color: "var(--qf-text-secondary)",
  fontVariantNumeric: "tabular-nums",
};

const NAME_INPUT: React.CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  fontFamily: "inherit",
  fontSize: 12,
  padding: "4px 7px",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--qf-accent)",
  borderRadius: 4,
  outline: "none",
};

const PENCIL: React.CSSProperties = {
  font: "inherit",
  fontSize: 11,
  padding: "3px 6px",
  border: 0,
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  flex: "0 0 auto",
};

/**
 * The eye and the ×, sized so the name is what shrinks.
 *
 * The panel is a fixed 250px, so a control added to this row is a sizing
 * change and has to be counted rather than assumed: 250 less 16 of padding is
 * 234, the chart count takes ~14, and three of these take 60 — which leaves the
 * name ~160px, or about 24 characters at 12px. It ellipsizes already
 * (`NAME_TEXT`), and there is deliberately no `overflow: auto` standing by to
 * catch a miscount.
 */
const ICON: React.CSSProperties = {
  ...PENCIL,
  width: 20,
  padding: "3px 0",
  textAlign: "center",
  // Nudged up: `⊘` and `⊙` sit slightly low against 12px text.
  lineHeight: "13px",
};

/**
 * Disabled, but still readable and still hoverable — the title is the whole
 * point of leaving it here rather than removing it.
 */
const DISABLED: React.CSSProperties = { opacity: 0.35, cursor: "default" };

/**
 * The lock switch when some charts are locked and some are not: the 🔒 at half
 * strength over a dashed underline — "half-filled" in the plan's mock, and not a
 * colour alone, which would read as a theme accent rather than a state.
 */
const MIXED: React.CSSProperties = {
  opacity: 0.55,
  textDecoration: "underline dashed var(--qf-text-muted)",
  textUnderlineOffset: 2,
};

/** A hidden layout's own row, marked as put away rather than merely unselected. */
const DIMMED: React.CSSProperties = { color: "var(--qf-text-muted)" };

/** `▸ 2 hidden` — a row of the list, not a heading, so it reads as one more entry. */
const DISCLOSURE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  width: "100%",
  font: "inherit",
  fontSize: 11,
  padding: "5px 8px",
  border: 0,
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text-muted)",
  cursor: "pointer",
  textAlign: "left",
};

const SYNC_GRID: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  columnGap: 8,
};

const TEMPLATE_ROW: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
};

function templateStyle(selected: boolean): React.CSSProperties {
  return {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    width: 68,
    font: "inherit",
    fontSize: 11,
    padding: "6px 4px",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: selected ? "var(--qf-accent)" : "transparent",
    borderRadius: 5,
    background: selected ? "var(--qf-selection)" : "transparent",
    color: selected ? "var(--qf-accent)" : "var(--qf-text-secondary)",
    cursor: "pointer",
  };
}

/**
 * Four equal columns rather than the arrangements' wrapping flex row: the sides
 * are one closed set, and a fixed width that wraps three and one reads as a
 * group of three plus an afterthought. A grid cannot wrap, whatever the labels
 * or the panel width turn out to be.
 */
const PLACEMENT_ROW: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: 6,
};

/** An arrangement button, sized by its grid column instead of by hand. */
function placementStyle(selected: boolean): React.CSSProperties {
  return { ...templateStyle(selected), width: "auto", boxSizing: "border-box" };
}

const CONFIRM: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 12,
  padding: "6px 4px",
  color: "var(--qf-text)",
};

const QUIET_BUTTON: React.CSSProperties = {
  font: "inherit",
  fontSize: 11,
  padding: "3px 8px",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};

const DANGER_BUTTON: React.CSSProperties = {
  ...QUIET_BUTTON,
  borderColor: "var(--qf-negative)",
  color: "var(--qf-negative)",
};

const NEW_BUTTON: React.CSSProperties = {
  display: "block",
  width: "100%",
  font: "inherit",
  fontSize: 12,
  padding: "6px 8px",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  cursor: "pointer",
  textAlign: "left",
};

/** New and Publish, side by side. See the comment where it is used. */
const NEW_ROW: React.CSSProperties = { display: "flex", gap: 6, alignItems: "stretch" };

/** A preset row: the name takes the width, the delete sits on the end. */
const PRESET_ROW: React.CSSProperties = { display: "flex", alignItems: "center" };

const PUBLISH_ERROR: React.CSSProperties = {
  fontSize: 11,
  color: "var(--qf-down)",
  padding: "6px 4px 0",
};

const TOGGLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 12,
  padding: "4px 4px",
  color: "var(--qf-text)",
  cursor: "pointer",
};
